import type { PlanCell, RawPlanCell } from '../types/plan';

/**
 * Normalize plan cells from API response.
 * CN-0012: Now using global coordinates directly (row, col).
 * Backend sends row/col as global coordinates.
 */
export const normalizePlanCells = (cells: RawPlanCell[] = []): PlanCell[] => {
  return cells
    .map((cell) => {
      // CN-0012: Use row/col directly as global coordinates
      // Support both camelCase and snake_case from backend
      const row = cell.row ?? cell.row_world;
      const col = cell.col ?? cell.col_world;
      
      if (!row || !col || !cell.kind) return null;
      
      return {
        row,
        col,
        kind: cell.kind,
        bedId: cell.bedId ?? cell.bed_id ?? null,
        plotId: cell.plotId ?? cell.plot_id ?? null,
        structuralObjectId: cell.structuralObjectId ?? cell.structural_object_id ?? null,
      } as PlanCell;
    })
    .filter(Boolean) as PlanCell[];
};

export const planCellKey = (row: number, col: number): string => `${row}:${col}`;

/**
 * Соседняя глобальная координата. Нуля в сетке нет: -1 и 1 — соседи.
 */
export const adjacentPlanCoordinate = (value: number, delta: -1 | 1): number => {
  if (value === 1 && delta === -1) return -1;
  if (value === -1 && delta === 1) return 1;
  return value + delta;
};

/**
 * Делит клетки на 4-связные группы (соседство по стороне, ноль пропускается).
 * Порядок детерминирован: группы — по первой клетке в порядке (row, col),
 * клетки внутри группы — по (row, col). Дубликаты схлопываются.
 */
export const splitFourConnectedCellGroups = (
  cells: Array<{ row: number; col: number }>,
): Array<Array<{ row: number; col: number }>> => {
  const byKey = new Map<string, { row: number; col: number }>();
  cells.forEach((cell) => byKey.set(planCellKey(cell.row, cell.col), { row: cell.row, col: cell.col }));
  const ordered = Array.from(byKey.values())
    .sort((left, right) => (left.row - right.row) || (left.col - right.col));

  const visited = new Set<string>();
  const groups: Array<Array<{ row: number; col: number }>> = [];
  ordered.forEach((start) => {
    const startKey = planCellKey(start.row, start.col);
    if (visited.has(startKey)) return;
    visited.add(startKey);
    const group: Array<{ row: number; col: number }> = [];
    const queue = [start];
    while (queue.length > 0) {
      const cell = queue.shift()!;
      group.push(cell);
      const neighbours = [
        { row: adjacentPlanCoordinate(cell.row, -1), col: cell.col },
        { row: adjacentPlanCoordinate(cell.row, 1), col: cell.col },
        { row: cell.row, col: adjacentPlanCoordinate(cell.col, -1) },
        { row: cell.row, col: adjacentPlanCoordinate(cell.col, 1) },
      ];
      neighbours.forEach((neighbour) => {
        const key = planCellKey(neighbour.row, neighbour.col);
        if (!byKey.has(key) || visited.has(key)) return;
        visited.add(key);
        queue.push(byKey.get(key)!);
      });
    }
    groups.push(group.sort((left, right) => (left.row - right.row) || (left.col - right.col)));
  });
  return groups;
};

/** Клетки образуют ровно одну 4-связную группу (пустой набор — нет). */
export const isFourConnectedCellGroup = (cells: Array<{ row: number; col: number }>): boolean =>
  splitFourConnectedCellGroups(cells).length === 1;
